/**
 * The Venom Altar bosses (PLAN §5). Timers are EventMap-style `{first, every}` taken from
 * the Altar of Fangs WCL timelines in research/analysis/boss_timers.txt (+22/23 run):
 *
 *   Rav'i            Ravenous Stomp 0.0/59.2 · Triple Shot 8.0/29.0 · Regurgitate 48.7/59.8
 *   Writhing Coil    Tail Scythe 7.0 · Toxic Atrophy 14.9/44.9 · Burrowing Charge 30.0/52.1
 *                    · Venom Jet 34.6/52.4 · split into Uncoiled Writhes (adds: Assimilation)
 *   Zul'jan          Ritual of the Fang 4.0/65.0 · Axegrinder 18.1/65.0 · Chop Down 30.0/32.5
 *                    · Boneslicer 36.0/16.1
 *
 * +encounters (docs/design/encounters.md §4): each boss has a signature mechanic, an HP-triggered
 * intermission and key breakpoints (+7 / +10 / +12, Tyrannical). Damage inside `mech(...)` specs is
 * the effective number (no enemy() knobs); top-level `hit`/`pulse` still go through the knobs.
 */
import type { AuraDef, EffectSpec, EncounterDef, EnemyAbility, NpcDef, Vec2, ZoneEffect } from '@keystone/sim';
import { ENCOUNTER, ENEMY, ENEMY_AURA, NPC } from '../ids';
import { npcMelee } from '../balance';
import { ARENA, RAVAGER_CARCASSES } from '../dungeons/venomAltarLayout';
import { areaHit, atKey, enemy, hit, keyHit, mech, pool, pulse, roomHit, telegraph, withAffix } from './common';

const ENRAGE_MS = 300_000;

/** Effective Ritual/teaching numbers below are % of a 40k DPS player at +2. */
const inset = (r: { x: number; y: number; w: number; h: number }, dx: number, dy: number): Vec2[] => [
  { x: r.x + dx, y: r.y + dy },
  { x: r.x + r.w - dx, y: r.y + dy },
  { x: r.x + dx, y: r.y + r.h - dy },
  { x: r.x + r.w - dx, y: r.y + r.h - dy },
];

// ---------------------------------------------------------------------------
// Boss 1: The Ravager — "Feed it nothing"
// ---------------------------------------------------------------------------

/** Tank poison stacked by every melee swing; Rending Maw consumes it. */
const septicBite: AuraDef = {
  id: ENEMY_AURA.septicBite,
  name: 'Septic Bite',
  kind: 'debuff',
  durationMs: 6000,
  stacking: 'stack',
  maxStacks: 8,
  dispelType: 'poison',
  mods: [{ stat: 'healingTakenPct', value: -0.03 }],
  periodic: { intervalMs: 1000, effects: [hit('nature', 250, { avoidable: false })] },
};
const septicBitePassive: AuraDef = {
  id: ENEMY_AURA.septicBitePassive,
  name: 'Septic Bite',
  kind: 'buff',
  durationMs: 0,
  stacking: 'ignore',
  flags: { hidden: true },
  procs: [{ on: 'meleeSwing', effects: [{ type: 'applyAura', aura: septicBite }] }],
};

const rendingMaw = enemy({
  id: ENEMY.rendingMaw,
  name: 'Rending Maw',
  school: 'physical',
  kind: 'tankbuster',
  description: 'Tank-buster, +8% per Septic Bite stack, consuming them. Defensive at high stacks.',
  cast: { type: 'cast', timeMs: 1500 },
  range: 10,
  timer: { first: 12000, every: 20000 },
  target: 'victim',
  priority: 3,
  counter: ['defensive'],
  warning: { text: 'Rending Maw', severity: 'danger' },
  effects: [
    { type: 'damage', school: 'physical', amount: { base: 21000, variance: 0.05, perStack: { auraId: ENEMY_AURA.septicBite, on: 'target', mult: 0.08 } } },
    { type: 'removeAura', auraId: ENEMY_AURA.septicBite },
  ],
});

const tripleShotLine = (barbed: boolean): EffectSpec =>
  telegraph({ type: 'line', length: 40, width: 4 }, 'caster', 2000, [
    keyHit('nature', 9000, 1.25, { avoidable: false }),
    ...(barbed
      ? [
          {
            type: 'applyAura' as const,
            aura: { id: ENEMY_AURA.barbed, name: 'Barbed', kind: 'debuff' as const, durationMs: 4000, stacking: 'refresh' as const, periodic: { intervalMs: 1000, effects: [hit('physical', 600, { avoidable: false })] } },
          },
        ]
      : []),
  ]);
const tripleShot = enemy({
  id: ENEMY.tripleShot,
  name: 'Triple Shot',
  school: 'nature',
  kind: 'frontal',
  description: 'Line shots at 2 random non-tanks (3 at +7, which also bleed). Spread around the boss; overlapping lines stack.',
  cast: { type: 'cast', timeMs: 1500 },
  timer: { first: 8000, every: 29000 },
  target: 'none',
  shape: { type: 'line', length: 40, width: 4 },
  priority: 2,
  counter: ['spread', 'dodge'],
  effects: [
    atKey(
      7,
      [mech({ kind: 'pick', rule: 'randomNotTank', count: 3, effects: [tripleShotLine(true)] })],
      [mech({ kind: 'pick', rule: 'randomNotTank', count: 2, effects: [tripleShotLine(false)] })],
    ),
  ],
});

/** +10: each whelp swing stacks Ravenous (+10% damage, enrage). Kill or kite them fast. */
const ravenous: AuraDef = {
  id: ENEMY_AURA.ravenous,
  name: 'Ravenous',
  kind: 'buff',
  durationMs: 10000,
  stacking: 'stack',
  maxStacks: 10,
  dispelType: 'enrage',
  mods: [{ stat: 'damageDonePct', value: 0.1 }],
};
const ravenousPassive: AuraDef = {
  id: ENEMY_AURA.ravenousPassive,
  name: 'Ravenous',
  kind: 'buff',
  durationMs: 0,
  stacking: 'ignore',
  flags: { hidden: true },
  procs: [{ on: 'meleeSwing', effects: [atKey(10, [{ type: 'applyAura', to: 'self', aura: ravenous }])] }],
};
const whelpFixate: AuraDef = { id: ENEMY_AURA.whelpFixate, name: 'Hunted', kind: 'debuff', durationMs: 8000, stacking: 'refresh' };
const ravenousStomp = enemy({
  id: ENEMY.ravenousStomp,
  name: 'Ravenous Stomp',
  school: 'physical',
  kind: 'summon',
  description: 'Party-wide stomp; 2 Ravenous Whelps (3 at +7) fixate random players for 8 s. Kite them to the tank and cleave. +10: every whelp swing stacks Ravenous (+10% damage).',
  cast: { type: 'cast', timeMs: 2000 },
  timer: { first: 2000, every: 59200 },
  target: 'none',
  priority: 4,
  counter: ['healerCd', 'kite', 'killAdd'],
  effects: [
    pulse('physical', 5000),
    atKey(
      7,
      [{ type: 'summon', npcId: NPC.ravenousWhelp, count: 3, radius: 6, anchor: 'caster' }],
      [{ type: 'summon', npcId: NPC.ravenousWhelp, count: 2, radius: 6, anchor: 'caster' }],
    ),
    mech({ kind: 'fixate', durationMs: 8000, adds: NPC.ravenousWhelp, rule: 'randomNotTank', aura: whelpFixate }),
  ],
});

const bilePool = (perOccupant: boolean): EffectSpec =>
  mech({
    kind: 'hazard',
    zone: pool('ravager.bile_pool', 3, 20000, 2500, { anchor: 'target' }),
    motion: { type: 'grow', perSec: 0.2, max: 6, ...(perOccupant ? { perOccupantSec: 0.5 } : {}) },
  });
/** Three bile bands toward the target, then a growing Bile Pool where it stood. */
const regurgitateWave: EffectSpec[] = [
  mech({
    kind: 'wave',
    form: 'line',
    bands: 3,
    bandWidth: 8,
    width: 10,
    startAt: 2,
    anchor: 'caster',
    delayMs: 1000,
    intervalMs: 600,
    effects: [
      hit('nature', 8000, { avoidable: false, isAoE: true }),
      { type: 'applyAura', aura: { id: ENEMY_AURA.bileBurn, name: 'Bile Burn', kind: 'debuff', durationMs: 6000, stacking: 'refresh', dispelType: 'poison', periodic: { intervalMs: 1000, effects: [hit('nature', 1000, { avoidable: false })] } } },
    ],
  }),
  atKey(10, [bilePool(true)], [bilePool(false)]),
];
const regurgitate = enemy({
  id: ENEMY.regurgitate,
  name: 'Regurgitate',
  school: 'nature',
  kind: 'groundAoE',
  description: 'Three bile waves roll toward a player (2 players at +12), 0.6 s apart; a Bile Pool spawns there and grows. Step through a wave once it fired. Tank drags the boss off pools.',
  cast: { type: 'cast', timeMs: 2000 },
  timer: { first: 20000, every: 30000 },
  target: 'random',
  shape: { type: 'rect', w: 10, h: 24 },
  priority: 1,
  counter: ['dodge'],
  effects: [atKey(12, [mech({ kind: 'pick', rule: 'random', count: 2, effects: regurgitateWave })], regurgitateWave)],
});

// Intermission: Carrion Feast ----------------------------------------------------

const gorgedHide: AuraDef = { id: ENEMY_AURA.gorgedHide, name: 'Gorged Hide', kind: 'buff', durationMs: 25000, stacking: 'unique', absorb: {} };
const feedingFrenzyStack: AuraDef = {
  id: ENEMY_AURA.feedingFrenzy,
  name: 'Feeding Frenzy',
  kind: 'buff',
  durationMs: 0,
  stacking: 'stack',
  maxStacks: 12,
  dispelType: 'enrage',
  mods: [{ stat: 'damageDonePct', value: 0.05 }],
};
const gorged: AuraDef = { id: ENEMY_AURA.gorged, name: 'Gorged', kind: 'buff', durationMs: 0, stacking: 'ignore', mods: [{ stat: 'damageDonePct', value: 0.5 }] };
const messyEater = mech({
  kind: 'soak',
  shape: { type: 'circle', radius: 3 },
  at: { around: 'caster', minDist: 6, maxDist: 10 },
  count: 1,
  delayMs: 2500,
  min: { base: 1, atKey: { 12: 2 } },
  damage: { school: 'nature', amount: { base: 12000 } },
  failEffects: [{ type: 'summon', npcId: NPC.ravenousWhelp, count: 1, radius: 0, anchor: 'point' }],
});
const feedingFrenzy = enemy({
  id: ENEMY.feedingFrenzy,
  name: 'Feeding Frenzy',
  school: 'physical',
  kind: 'buff',
  description:
    'Channel (24 s) behind Gorged Hide: not interruptible until the shield breaks. Each 2 s: +5% damage and a Messy Eater spatter (soak it or a whelp spawns). If it finishes: Gorged (+50% damage) and a heavy party hit.',
  cast: { type: 'channel', durationMs: 24000, ticks: 12 },
  interruptible: true,
  interruptibleWhen: { casterLacksAura: ENEMY_AURA.gorgedHide },
  timer: { first: 1200, every: 60000 },
  target: 'self',
  priority: 10,
  kickPriority: 1,
  counter: ['burstShield', 'interrupt', 'soak'],
  warning: { text: 'Break the shield, then interrupt!', severity: 'deadly' },
  channelStartEffects: [mech({ kind: 'shield', aura: gorgedHide, pctMaxHp: { base: 0.04, atKey: { 7: 0.045, 10: 0.05 }, affixMult: { tyrannical: 1.2 } } })],
  effects: [
    {
      type: 'conditional',
      cond: { type: 'selfHasAura', auraId: ENEMY_AURA.feedingFrenzy, minStacks: 11 },
      then: [{ type: 'applyAura', to: 'self', aura: gorged }, roomHit('physical', 15000)],
    },
    { type: 'applyAura', to: 'self', aura: feedingFrenzyStack },
    messyEater,
  ],
});

// Phase 2 ------------------------------------------------------------------------

const pounceTrail: ZoneEffect = pool('ravager.bile_trail', 0, 10000, 2500, { anchor: 'caster' });
const pounce = (trail: boolean): EffectSpec =>
  mech({
    kind: 'charge',
    rule: 'farthest',
    width: 5,
    overshoot: 4,
    delayMs: 2500,
    aimFollowMs: 1000,
    speed: 40,
    intent: 'avoid',
    effects: [keyHit('physical', 15000, 1.2, { avoidable: false }), mech({ kind: 'knockback', distance: 10, durationMs: 500 })],
    ...(trail ? { trail: pounceTrail } : {}),
  });
const huntingPounce = enemy({
  id: ENEMY.huntingPounce,
  name: 'Hunting Pounce',
  school: 'physical',
  kind: 'charge',
  description: 'Leaps along a line at the farthest player (aim locks 1 s in). Sidestep the line. +7: leaves a bile trail.',
  cast: { type: 'cast', timeMs: 1500 },
  timer: { first: 5000, every: 24000 },
  target: 'none',
  priority: 4,
  counter: ['dodge'],
  warning: { text: 'Hunting Pounce', severity: 'danger' },
  effects: [atKey(7, [pounce(true)], [pounce(false)])],
});

// ---------------------------------------------------------------------------
// Boss 2: The Coil — "Kick the chain, break the tethers"
// ---------------------------------------------------------------------------

const syncVenom = (id: string, healCut: boolean): AuraDef => ({
  id,
  name: 'Synchronized Venom',
  kind: 'debuff',
  durationMs: 12000,
  stacking: 'refresh',
  periodic: { intervalMs: 2000, effects: [hit('nature', 1200, { avoidable: false })] },
  ...(healCut ? { mods: [{ stat: 'healingTakenPct' as const, value: -0.1 }] } : {}),
});
const synchronizedVenom = enemy({
  id: ENEMY.synchronizedVenom,
  name: 'Synchronized Venom',
  school: 'nature',
  kind: 'pulse',
  description: 'Healer check: 12 s of venom on everyone (not dispellable). +7: also −10% healing taken.',
  cast: { type: 'cast', timeMs: 1000 },
  timer: { first: 1000, every: 45000 },
  target: 'none',
  priority: 5,
  counter: ['healerCd'],
  effects: [
    mech({
      kind: 'pick',
      rule: 'all',
      effects: [atKey(7, [{ type: 'applyAura', aura: syncVenom(ENEMY_AURA.syncVenomEmpowered, true) }], [{ type: 'applyAura', aura: syncVenom(ENEMY_AURA.syncVenom, false) }])],
    }),
  ],
});

const tailScythe = enemy({
  id: ENEMY.tailScythe,
  name: 'Tail Scythe',
  school: 'physical',
  kind: 'frontal',
  description: '120° frontal at the tank. Tank faces it away; melee stay at the sides. +7: knocks the tank back.',
  cast: { type: 'cast', timeMs: 2000 },
  range: 12,
  timer: { first: 7000, every: 18000 },
  target: 'victim',
  shape: { type: 'cone', range: 12, angle: (2 * Math.PI) / 3 },
  priority: 3,
  counter: ['tankFacing', 'dodge'],
  effects: [
    { type: 'damage', to: 'area', area: { anchor: 'caster', shape: { type: 'cone', range: 12, angle: (2 * Math.PI) / 3 }, affects: 'enemies' }, school: 'physical', amount: { base: Math.round(16000 / 2.1), variance: 0.05 }, avoidable: false },
    atKey(7, [mech({ kind: 'knockback', distance: 6, durationMs: 400 })]),
  ],
});

/** Toxic Atrophy: jumps to the nearest ally (10 yd) when dispelled. Holder steps away, then dispel. */
const toxicAtrophyAura: AuraDef = {
  id: ENEMY_AURA.toxicAtrophy,
  name: 'Toxic Atrophy',
  kind: 'debuff',
  durationMs: 16000,
  stacking: 'refresh',
  dispelType: 'poison',
  mods: [{ stat: 'healingTakenPct', value: -0.3 }],
  periodic: { intervalMs: 2000, effects: [hit('nature', 2200, { avoidable: false })] },
  jumpOnDispel: { radius: 10, effects: [atKey(10, [hit('nature', 6000, { avoidable: false })])] },
  hazard: { radius: 10, intent: 'isolate' },
};
const atrophyBase = {
  name: 'Toxic Atrophy',
  school: 'nature' as const,
  kind: 'cc' as const,
  description: 'Interruptible chain. Poison on a random player: damage over time and −30% healing taken. Dispelling it makes it jump to the nearest ally within 10 yd.',
  cast: { type: 'cast' as const, timeMs: 1500 },
  interruptible: true,
  target: 'random' as const,
  dispellable: 'poison' as const,
  priority: 2,
  kickPriority: 2,
  counter: ['interrupt', 'dispel', 'spread'] as EnemyAbility['counter'],
  effects: [{ type: 'applyAura' as const, aura: toxicAtrophyAura }],
};
const toxicAtrophy = enemy({ ...atrophyBase, id: ENEMY.toxicAtrophy, timer: { first: 14900, every: 44900, chain: { count: { base: 2, atKey: { 7: 3 } }, gapMs: 400 } } });
const toxicAtrophyP2 = enemy({ ...atrophyBase, id: ENEMY.toxicAtrophyP2, timer: { first: 20000, every: 45000, chain: { count: { base: 1, atKey: { 7: 2 } }, gapMs: 400 } } });
/** The writhes' copy: single casts, first rolled over 8-12 s so it doesn't sync with the Coil's. */
const writheToxicAtrophy = enemy({ ...atrophyBase, id: ENEMY.writheToxicAtrophy, timer: { first: 8000, firstMax: 12000, every: 20000 }, kickPriority: 3 });

const burrowingCharge = enemy({
  id: ENEMY.burrowingCharge,
  name: 'Burrowing Charge',
  school: 'physical',
  kind: 'charge',
  description: 'Burrows along a 45 yd line toward a random player, knocking back anyone hit. Step out sideways.',
  cast: { type: 'cast', timeMs: 1500 },
  timer: { first: 30000, every: 52100 },
  target: 'random',
  shape: { type: 'line', length: 45, width: 6 },
  priority: 4,
  counter: ['dodge'],
  effects: [
    mech({
      kind: 'charge',
      rule: 'primary',
      width: 6,
      length: 45,
      delayMs: 2500,
      aimFollowMs: 800,
      speed: 35,
      effects: [keyHit('physical', 14000, 1.3, { avoidable: false }), mech({ kind: 'knockback', distance: 8, durationMs: 500 })],
    }),
  ],
});

const venomJet = enemy({
  id: ENEMY.venomJet,
  name: 'Venom Jet',
  school: 'nature',
  kind: 'frontal',
  description: 'A 35 yd cone toward a non-tank player, plus a poison.',
  cast: { type: 'cast', timeMs: 1000 },
  timer: { first: 34600, every: 52400 },
  target: 'randomNotTank',
  shape: { type: 'cone', range: 35, angle: Math.PI / 3 },
  priority: 1,
  counter: ['dodge'],
  effects: [
    telegraph({ type: 'cone', range: 35, angle: Math.PI / 3 }, 'caster', 2000, [
      hit('nature', 16000, { avoidable: false }),
      { type: 'applyAura', aura: { id: ENEMY_AURA.venomJetPoison, name: 'Venom Jet', kind: 'debuff', durationMs: 6000, stacking: 'refresh', dispelType: 'poison', periodic: { intervalMs: 1000, effects: [hit('nature', 800, { avoidable: false })] } } },
    ]),
  ],
});

// Intermission: Death Rattle -----------------------------------------------------

const writheFixate: AuraDef = { id: ENEMY_AURA.writheFixate, name: 'Writhe Fixate', kind: 'debuff', durationMs: 10000, stacking: 'refresh' };
const deathRattleTether = (slow: boolean): EffectSpec =>
  mech({
    kind: 'tether',
    rule: 'all',
    breakAt: 18,
    durationMs: 5000,
    failEffects: [hit('nature', 26000, { avoidable: false })],
    ...(slow ? { whileTethered: { id: ENEMY_AURA.sluggishVenom, name: 'Sluggish Venom', kind: 'debuff', durationMs: 5000, stacking: 'refresh', mods: [{ stat: 'moveSpeedPct', value: -0.3 }] } } : {}),
  });
const deathRattle = enemy({
  id: ENEMY.deathRattle,
  name: 'Death Rattle',
  school: 'nature',
  kind: 'pulse',
  description: 'Tethers every player. Get more than 18 yd from the Coil before it ends or take a lethal hit. +7: tethers slow.',
  cast: { type: 'channel', durationMs: 5000, ticks: 1 },
  timer: { first: 1600, every: 60000 },
  target: 'none',
  priority: 10,
  counter: ['runOut'],
  warning: { text: 'Run out! (18 yd)', severity: 'deadly' },
  channelStartEffects: [atKey(7, [deathRattleTether(true)], [deathRattleTether(false)])],
  effects: [{ type: 'threat', to: 'self', amount: 0 }],
});

const recoil = enemy({
  id: ENEMY.recoil,
  name: 'Recoil',
  school: 'nature',
  kind: 'heal',
  description: 'After 25 s (then every 20 s) a surviving Writhe lashes back toward the Coil along a line (12k) and heals it 4%. Kill writhes first.',
  cast: { type: 'cast', timeMs: 2000 },
  timer: { first: 25000, every: 20000 },
  target: 'lowestHpAlly',
  range: 80,
  priority: 10,
  counter: ['killAdd', 'dodge'],
  warning: { text: 'Recoil', severity: 'danger' },
  effects: [
    { type: 'heal', amount: { pctTargetMaxHp: 0.04 } },
    {
      type: 'damage',
      to: 'area',
      area: { anchor: 'caster', shape: { type: 'line', length: 40, width: 4 }, affects: 'all', excludePrimary: true },
      school: 'nature',
      amount: { base: Math.round(12000 / 2.1) },
      avoidable: false,
      if: { type: 'targetIsEnemy' },
    },
  ],
});

// Phase 2 ------------------------------------------------------------------------

const torrent = (turnDeg: number): EffectSpec =>
  mech({
    kind: 'hazard',
    zone: {
      type: 'zone',
      zoneId: 'coil.venom_torrent',
      shape: { type: 'line', length: 30, width: 5 },
      anchor: 'caster',
      durationMs: 5000,
      affects: 'enemies',
      uniquePerCaster: false,
      tick: { intervalMs: 500, effects: [hit('nature', 2000, { avoidable: false, isAoE: true })] },
    },
    motion: { type: 'aim', turnRate: (turnDeg * Math.PI) / 180, rule: 'primary' },
    intent: 'deadly',
  });
const venomTorrent = enemy({
  id: ENEMY.venomTorrent,
  name: 'Venom Torrent',
  school: 'nature',
  kind: 'frontal',
  description: 'A 5 s beam that turns toward its target at 30°/s (+10: 40°, +12: 50°). The target circles close to the boss; everyone else stays opposite.',
  cast: { type: 'cast', timeMs: 1500 },
  timer: { first: 15000, every: 35000 },
  target: 'randomNotTank',
  shape: { type: 'line', length: 30, width: 5 },
  priority: 6,
  counter: ['kite'],
  warning: { text: 'Venom Torrent', severity: 'danger' },
  effects: [atKey(12, [torrent(50)], [atKey(10, [torrent(40)], [torrent(30)])])],
});

const coilingVortex = enemy({
  id: ENEMY.coilingVortex,
  name: 'Coiling Vortex',
  school: 'nature',
  kind: 'pulse',
  description: '+7 only. 4 s channel pulling everyone in; a venom ring burns around the Coil. Walk out against the pull.',
  cast: { type: 'channel', durationMs: 4000, ticks: 4 },
  timer: { first: 25000, every: 40000 },
  target: 'none',
  minKeyLevel: 7,
  priority: 7,
  counter: ['runOut'],
  warning: { text: 'Coiling Vortex', severity: 'danger' },
  channelStartEffects: [
    mech({ kind: 'pull', durationMs: 4000, speed: 2.5, radius: 50, minDist: 3 }),
    {
      type: 'zone',
      zoneId: 'coil.vortex_ring',
      shape: { type: 'circle', radius: 6 },
      anchor: 'caster',
      durationMs: 4000,
      affects: 'enemies',
      tick: { intervalMs: 500, effects: [hit('nature', 1500, { avoidable: false, isAoE: true })] },
    },
  ],
  effects: [{ type: 'threat', to: 'self', amount: 0 }],
});

// ---------------------------------------------------------------------------
// Boss 3: Chieftain Vashk — "Soak, then clean up"
// ---------------------------------------------------------------------------

/** Ritual Venom: stacks from beam soaks; explodes on expiry (4k × stacks, 8 yd). Bone Shards clear it. */
const ritualVenom: AuraDef = {
  id: ENEMY_AURA.ritualVenom,
  name: 'Ritual Venom',
  kind: 'debuff',
  durationMs: 25000,
  stacking: 'stack',
  maxStacks: 12,
  periodic: { intervalMs: 1000, effects: [hit('nature', 300, { avoidable: false })] },
  // +10: the explosion leaves a 15 s venom pool (r 4) at the holder's feet.
  explodeOnExpire: { radius: 8, damage: { school: 'nature', amount: { base: 4000 }, perStack: true }, pool: pool('chieftain.ritual_pool', 4, 15000, 2000), poolMinKey: 10 },
  hazard: { radius: 8, intent: 'spread', growsToExpiry: true },
};
const fangEmpowerment: AuraDef = { id: ENEMY_AURA.fangEmpowerment, name: 'Fang Empowerment', kind: 'buff', durationMs: 30000, stacking: 'stack', maxStacks: 5, mods: [{ stat: 'damageDonePct', value: 0.2 }] };
const BRAZIER = 'brazier';
/** Tyrannical: Ritual Venom lasts 20% less, so there is less time to clear it in Bone Shards. */
const RITUAL_VENOM_TYRANNICAL_MS = Math.round(25000 * 0.8);
const applyRitualVenom = (stacks: number): EffectSpec =>
  withAffix('tyrannical', [{ type: 'applyAura', aura: ritualVenom, stacks, durationMs: RITUAL_VENOM_TYRANNICAL_MS }], [{ type: 'applyAura', aura: ritualVenom, stacks }]);
const ritualBeams = (count: number | { base: number; atKey: Record<number, number> }): EffectSpec =>
  mech({
    kind: 'soak',
    shape: { type: 'line', length: 1, width: 3 },
    at: { marker: BRAZIER, aimAtCaster: true },
    count,
    delayMs: 4000,
    min: 1,
    split: false,
    damage: { school: 'nature', amount: { base: 6000 } },
    effects: [atKey(12, [applyRitualVenom(3)], [applyRitualVenom(2)])],
    failEffects: [roomHit('nature', 6000), { type: 'applyAura', to: 'self', aura: fangEmpowerment }],
  });
const ritualBase = {
  name: 'Ritual of the Fang',
  school: 'nature' as const,
  kind: 'pulse' as const,
  description: 'Beams from the braziers to Vashk: one player in each beam (3, 4 at +7; 4 in phase 2). Soakers get 2 Ritual Venom. An unsoaked beam pulses the party and empowers Vashk.',
  cast: { type: 'cast' as const, timeMs: 3000 },
  target: 'none' as const,
  priority: 4,
  counter: ['soak'] as EnemyAbility['counter'],
  warning: { text: 'Soak the beams!', severity: 'deadly' as const },
};
const ritualOfTheFang = enemy({ ...ritualBase, id: ENEMY.ritualOfTheFang, timer: { first: 4000, every: 65000 }, effects: [ritualBeams({ base: 3, atKey: { 7: 4 } })] });
const ritualOfTheFangP2 = enemy({ ...ritualBase, id: ENEMY.ritualOfTheFangP2, timer: { first: 10000, every: 45000 }, effects: [ritualBeams(4)] });

const boneShards = (stacks: number): EffectSpec =>
  mech({
    kind: 'soak',
    shape: { type: 'circle', radius: 3 },
    at: { around: 'caster', minDist: 7, maxDist: 9 },
    count: 2,
    delayMs: 6000,
    trigger: 'enter',
    consumes: { auraId: ENEMY_AURA.ritualVenom, stacks },
    eligible: { hasAura: ENEMY_AURA.ritualVenom },
    damage: { school: 'physical', amount: { base: 4000 } },
    intent: 'soak',
  });
const boneslicer = enemy({
  id: ENEMY.boneslicer,
  name: 'Boneslicer',
  school: 'physical',
  kind: 'tankbuster',
  description: 'Tank hit + bleed; drops 2 Bone Shards. A shard clears up to 4 Ritual Venom stacks (2 at +10) from the first venom holder who steps in.',
  cast: { type: 'cast', timeMs: 1000 },
  range: 10,
  timer: { first: 14000, every: 16100 },
  target: 'victim',
  priority: 1,
  counter: ['defensive', 'soak'],
  effects: [
    hit('physical', 13300),
    {
      type: 'applyAura',
      aura: {
        id: ENEMY_AURA.boneslice,
        name: 'Boneslice',
        kind: 'debuff',
        durationMs: 8000,
        stacking: 'stack',
        maxStacks: 3,
        periodic: { intervalMs: 1000, effects: [hit('physical', 400, { avoidable: false })] },
      },
    },
    atKey(10, [boneShards(2)], [boneShards(4)]),
  ],
});

const chopDown = enemy({
  id: ENEMY.chopDown,
  name: 'Chop Down',
  school: 'physical',
  kind: 'tankbuster',
  description: 'Three-hit tank combo. Defensive! If the tank carries Ritual Venom, the last hit leaves a venom puddle.',
  cast: { type: 'cast', timeMs: 2500 },
  range: 10,
  timer: { first: 30000, every: 32500 },
  target: 'victim',
  priority: 2,
  counter: ['defensive'],
  warning: { text: 'Chop Down', severity: 'danger' },
  effects: [
    hit('physical', 13300),
    {
      type: 'applyAura',
      aura: { id: ENEMY_AURA.chopDown, name: 'Chop Down', kind: 'debuff', durationMs: 1300, stacking: 'refresh', flags: { hidden: true }, periodic: { intervalMs: 600, effects: [hit('physical', 10000, { avoidable: false })] } },
    },
    {
      type: 'conditional',
      cond: { type: 'targetHasAura', auraId: ENEMY_AURA.ritualVenom },
      then: [pool('chieftain.venom_puddle', 4, 20000, 2000, { anchor: 'target' })],
    },
  ],
});

const axes = (speed: number): EffectSpec =>
  mech({
    kind: 'hazard',
    count: { base: 3, atKey: { 10: 4 } },
    zone: {
      type: 'zone',
      zoneId: 'chieftain.axe',
      shape: { type: 'circle', radius: 2.5 },
      anchor: 'caster',
      durationMs: 10000,
      affects: 'enemies',
      uniquePerCaster: false,
      tick: { intervalMs: 1000, effects: [keyHit('physical', 9000, 1.4, { avoidable: false, isAoE: true }), mech({ kind: 'knockback', distance: 4, durationMs: 300, from: 'point' })] },
    },
    motion: { type: 'bounce', speed, heading: 'spread' },
  });
const axegrinder = enemy({
  id: ENEMY.axegrinder,
  name: 'Axegrinder',
  school: 'physical',
  kind: 'groundAoE',
  description: '3 spinning axes (4 at +10) bounce off the walls for 10 s. They are slower than you: watch the angles.',
  cast: { type: 'cast', timeMs: 2000 },
  timer: { first: 18100, every: 50000 },
  target: 'none',
  priority: 3,
  counter: ['dodge'],
  effects: [atKey(10, [axes(6.5)], [axes(5)])],
});

const bloodroarHit: EffectSpec = {
  type: 'damage',
  to: 'area',
  area: { anchor: 'caster', shape: { type: 'cone', range: 30, angle: (5 * Math.PI) / 3 }, affects: 'enemies' },
  school: 'physical',
  amount: { base: Math.round(20000 / 2.1), variance: 0.05 },
  avoidable: false,
};
const bloodroar = enemy({
  id: ENEMY.bloodroar,
  name: 'Bloodroar',
  school: 'physical',
  kind: 'frontal',
  description: 'Vashk locks his facing and roars in a 300° cone. Only the 60° wedge directly behind him is safe — tank included. +10: bleeds.',
  cast: { type: 'cast', timeMs: 3000 },
  // Design said 15/40, but phase 2's first Ritual beams resolve at ~17 s with the soakers out at the
  // braziers, 25+ yd from the wedge: a roar at 15–18 s is an unreactable double. Same period as the
  // Ritual (45 s), offset so the roar always lands ~13 s after a soak and ~22 s before the next.
  timer: { first: 30000, every: 45000 },
  target: 'none',
  lockFacing: true,
  shape: { type: 'cone', range: 30, angle: (5 * Math.PI) / 3 },
  priority: 6,
  counter: ['behindCaster'],
  warning: { text: 'Get behind Vashk!', severity: 'deadly' },
  effects: [
    bloodroarHit,
    atKey(10, [
      {
        type: 'applyAura',
        to: 'area',
        area: { anchor: 'caster', shape: { type: 'cone', range: 30, angle: (5 * Math.PI) / 3 }, affects: 'enemies' },
        aura: { id: ENEMY_AURA.bloodroarBleed, name: 'Bloodroar', kind: 'debuff', durationMs: 6000, stacking: 'refresh', periodic: { intervalMs: 1000, effects: [hit('physical', 1500, { avoidable: false })] } },
      },
    ]),
  ],
});

const bloodrage = enemy({
  id: ENEMY.bloodrage,
  name: 'Bloodrage',
  school: 'physical',
  kind: 'buff',
  description: 'At 20%: +25% damage and haste. Kill fast. +10: also pulses the party.',
  timer: { first: 0, every: 1000 },
  target: 'self',
  once: true,
  hpWindow: { max: 0.2 },
  priority: 20,
  counter: ['healerCd', 'defensive'],
  warning: { text: 'BLOODRAGE', severity: 'danger' },
  effects: [
    atKey(
      10,
      [
        {
          type: 'applyAura',
          to: 'self',
          aura: {
            id: ENEMY_AURA.bloodrageEmpowered,
            name: 'Bloodrage',
            kind: 'buff',
            durationMs: 0,
            stacking: 'ignore',
            dispelType: 'enrage',
            mods: [
              { stat: 'damageDonePct', value: 0.25 },
              { stat: 'hastePct', value: 0.25 },
            ],
            periodic: { intervalMs: 2000, effects: [roomHit('physical', 1500)] },
          },
        },
      ],
      [
        {
          type: 'applyAura',
          to: 'self',
          aura: {
            id: ENEMY_AURA.bloodrage,
            name: 'Bloodrage',
            kind: 'buff',
            durationMs: 0,
            stacking: 'ignore',
            dispelType: 'enrage',
            mods: [
              { stat: 'damageDonePct', value: 0.25 },
              { stat: 'hastePct', value: 0.25 },
            ],
          },
        },
      ],
    ),
  ],
});

// Intermission: Blood Offering ---------------------------------------------------

const bloodSacrifice = enemy({
  id: ENEMY.bloodSacrifice,
  name: 'Blood Sacrifice',
  school: 'shadow',
  kind: 'cc',
  description: 'Interruptible. An 8k healing absorb on every player (12k at +7).',
  cast: { type: 'cast', timeMs: 2500 },
  interruptible: true,
  timer: { first: 1500, every: 6000, jitter: 500 },
  target: 'none',
  priority: 5,
  kickPriority: 1,
  counter: ['interrupt', 'killAdd'],
  effects: [
    mech({
      kind: 'pick',
      rule: 'all',
      effects: [
        mech({
          kind: 'healAbsorb',
          aura: { id: ENEMY_AURA.bloodSacrifice, name: 'Blood Sacrifice', kind: 'debuff', durationMs: 20000, stacking: 'unique', healAbsorb: true },
          amount: { base: 8000 },
          scale: { base: 1, atKey: { 7: 1.5 } },
        }),
      ],
    }),
  ],
});

/** Bosses take the Blood Offering / Burrowed immunity while rooted in place. */
const immuneRooted = (id: string, name: string): AuraDef => ({
  id,
  name,
  kind: 'buff',
  durationMs: 0,
  stacking: 'unique',
  flags: { immuneDamage: true },
  mods: [{ stat: 'moveSpeedPct', value: -1 }],
});

// Legacy (pre-rework) abilities: no NPC uses them any more, but their ids stay resolvable for the
// HUD icon table and old logs.
const assimilate = enemy({
  id: ENEMY.assimilate,
  name: 'Assimilate',
  school: 'nature',
  kind: 'heal',
  description: 'Legacy. Interruptible. An Uncoiled Writhe mends the most injured coil for 8% of its health.',
  cast: { type: 'cast', timeMs: 4000 },
  interruptible: true,
  timer: { first: 8000, every: 15000, jitter: 1000 },
  target: 'lowestHpAlly',
  priority: 5,
  effects: [{ type: 'heal', amount: { pctTargetMaxHp: 0.08 } }],
});
const boneCyclone = enemy({
  id: ENEMY.boneCyclone,
  name: 'Bone Cyclone',
  school: 'physical',
  kind: 'groundAoE',
  description: 'Legacy. Spins after 3 s, hitting everyone within 10 yd.',
  cast: { type: 'cast', timeMs: 1000 },
  timer: { first: 18100, every: 65000 },
  target: 'none',
  shape: { type: 'circle', radius: 10 },
  effects: [
    telegraph({ type: 'circle', radius: 10 }, 'caster', 3000, [hit('physical', 15000, { avoidable: false })]),
    { type: 'applyAura', aura: { id: ENEMY_AURA.fangRitual, name: 'Ritual of the Fang', kind: 'debuff', durationMs: 10000, stacking: 'refresh', periodic: { intervalMs: 2000, effects: [hit('nature', 1800, { avoidable: false })] } } },
  ],
});

export const BOSS_ABILITIES: EnemyAbility[] = [
  assimilate,
  boneCyclone,
  rendingMaw,
  tripleShot,
  ravenousStomp,
  regurgitate,
  feedingFrenzy,
  huntingPounce,
  synchronizedVenom,
  tailScythe,
  toxicAtrophy,
  toxicAtrophyP2,
  burrowingCharge,
  venomJet,
  deathRattle,
  venomTorrent,
  coilingVortex,
  writheToxicAtrophy,
  recoil,
  ritualOfTheFang,
  ritualOfTheFangP2,
  boneslicer,
  chopDown,
  axegrinder,
  bloodroar,
  bloodrage,
  bloodSacrifice,
];

// ---------------------------------------------------------------------------
// NPCs
// ---------------------------------------------------------------------------

const boss = { radius: 3, combatReach: 4, moveSpeed: 7, ccImmune: true, tauntDr: true } as const;

export const BOSS_NPCS: NpcDef[] = [
  {
    id: NPC.ravager,
    name: 'The Ravager',
    tier: 'boss',
    hpMult: 0.9, // (harness +10 pacing: was 0.8) Rav'i 20.4M, the lightest boss (+encounters: −6% for the Carrion Feast intermission)
    forces: 0,
    melee: npcMelee(2200),
    abilities: [ENEMY.rendingMaw, ENEMY.tripleShot, ENEMY.ravenousStomp, ENEMY.regurgitate, ENEMY.feedingFrenzy, ENEMY.huntingPounce],
    auras: [septicBitePassive],
    ...boss,
    color: '#d35400',
  },
  {
    id: NPC.ravenousWhelp,
    name: 'Ravenous Whelp',
    tier: 'fodder',
    hpMult: 0.4,
    forces: 0,
    melee: npcMelee(350, 1500),
    abilities: [],
    auras: [ravenousPassive],
    radius: 0.7,
    combatReach: 1,
    moveSpeed: 6.5,
    color: '#e67e22',
  },
  {
    id: NPC.theCoil,
    name: 'The Coil',
    tier: 'boss',
    hpMult: 0.74, // (harness +10 pacing: was 0.66) plus Uncoiled Writhes at 50% (their damage is shared with the Coil)
    forces: 0,
    melee: npcMelee(2200),
    abilities: [ENEMY.synchronizedVenom, ENEMY.tailScythe, ENEMY.toxicAtrophy, ENEMY.toxicAtrophyP2, ENEMY.burrowingCharge, ENEMY.venomJet, ENEMY.deathRattle, ENEMY.venomTorrent, ENEMY.coilingVortex],
    ...boss,
    color: '#16a085',
  },
  {
    id: NPC.uncoiledWrithe,
    name: 'Uncoiled Writhe',
    tier: 'big',
    hpMult: 0.9,
    forces: 0,
    melee: npcMelee(1200),
    abilities: [ENEMY.writheToxicAtrophy, ENEMY.recoil],
    damageLink: { to: 'boss', pct: 0.5 },
    radius: 1.6,
    combatReach: 2.5,
    moveSpeed: 6.5,
    color: '#1abc9c',
  },
  {
    id: NPC.chieftain,
    name: 'Chieftain Vashk',
    tier: 'boss',
    hpMult: 0.95, // (harness +10 pacing: was 0.92) Zul'jan 25.9M (+encounters: −8% for the immune Blood Offering intermission)
    forces: 0,
    melee: npcMelee(1800),
    abilities: [ENEMY.ritualOfTheFang, ENEMY.ritualOfTheFangP2, ENEMY.boneslicer, ENEMY.chopDown, ENEMY.axegrinder, ENEMY.bloodroar, ENEMY.bloodrage],
    ...boss,
    color: '#c0392b',
  },
  {
    id: NPC.sacrificialCultist,
    name: 'Sacrificial Cultist',
    tier: 'caster',
    hpMult: 0.6,
    forces: 0,
    melee: npcMelee(300),
    abilities: [ENEMY.bloodSacrifice],
    radius: 1,
    combatReach: 1.5,
    moveSpeed: 7,
    preferredRange: 20,
    color: '#8e1b1b',
  },
];

// ---------------------------------------------------------------------------
// Encounters
// ---------------------------------------------------------------------------

const writheSpawn: EffectSpec[] = [
  { type: 'summon', npcId: NPC.uncoiledWrithe, count: 2, radius: 12, anchor: 'caster' },
  withAffix('tyrannical', [{ type: 'summon', npcId: NPC.uncoiledWrithe, count: 1, radius: 12, anchor: 'caster' }]),
  mech({ kind: 'fixate', durationMs: 10000, adds: NPC.uncoiledWrithe, rule: 'random', aura: writheFixate }),
];

export const ENCOUNTERS: EncounterDef[] = [
  {
    id: ENCOUNTER.ravager,
    name: 'The Ravager',
    bossNpcIds: [NPC.ravager],
    phases: [
      { untilHpPct: 0.55, abilities: [ENEMY.ravenousStomp, ENEMY.rendingMaw, ENEMY.tripleShot, ENEMY.regurgitate] },
      {
        // Carrion Feast: leap to a carcass, channel Feeding Frenzy behind Gorged Hide.
        abilities: [ENEMY.feedingFrenzy],
        intermission: {
          maxMs: 28000,
          endsWhen: [{ interrupted: ENEMY.feedingFrenzy }, { castCompleted: ENEMY.feedingFrenzy }],
          moveTo: { marker: 'carcass', pick: 'nearest' },
          leapMs: 1000,
          landing: [areaHit('physical', 8000, 6)],
          onEnd: [{ type: 'removeAura', to: 'self', auraId: ENEMY_AURA.gorgedHide }],
        },
      },
      {
        abilities: [ENEMY.huntingPounce, ENEMY.rendingMaw, ENEMY.tripleShot, ENEMY.regurgitate, ENEMY.ravenousStomp],
        // Design §4.1 phase 2 openers: Pounce first, the next Stomp only 30 s after the feast.
        firstMs: { [ENEMY.rendingMaw]: 8000, [ENEMY.tripleShot]: 14000, [ENEMY.regurgitate]: 22000, [ENEMY.ravenousStomp]: 30000 },
      },
    ],
    enrageMs: ENRAGE_MS,
    arena: ARENA.ravager,
    markers: { carcass: RAVAGER_CARCASSES },
  },
  {
    id: ENCOUNTER.coil,
    name: 'The Coil',
    bossNpcIds: [NPC.theCoil],
    phases: [
      { untilHpPct: 0.5, abilities: [ENEMY.synchronizedVenom, ENEMY.tailScythe, ENEMY.toxicAtrophy, ENEMY.burrowingCharge, ENEMY.venomJet] },
      {
        // Death Rattle: burrow to the centre, tether everyone; Uncoiled Writhes emerge after.
        abilities: [ENEMY.deathRattle],
        intermission: {
          maxMs: 12000,
          endsWhen: [{ castCompleted: ENEMY.deathRattle }],
          moveTo: 'arenaCenter',
          leapMs: 1500,
          bossAura: immuneRooted(ENEMY_AURA.burrowed, 'Burrowed'),
          onEnd: writheSpawn,
          onTimeout: writheSpawn,
        },
      },
      {
        abilities: [ENEMY.tailScythe, ENEMY.synchronizedVenom, ENEMY.toxicAtrophyP2, ENEMY.venomTorrent, ENEMY.coilingVortex],
        // Design §4.2 phase 2 openers (writhes are still up: venom waits 10 s).
        firstMs: { [ENEMY.tailScythe]: 6000, [ENEMY.synchronizedVenom]: 10000 },
      },
    ],
    enrageMs: ENRAGE_MS,
    arena: ARENA.coil,
  },
  {
    id: ENCOUNTER.chieftain,
    name: 'Chieftain Vashk',
    bossNpcIds: [NPC.chieftain],
    phases: [
      { untilHpPct: 0.5, abilities: [ENEMY.ritualOfTheFang, ENEMY.boneslicer, ENEMY.axegrinder, ENEMY.chopDown] },
      {
        // Blood Offering: Vashk is immune at the altar; two cultists put healing absorbs on the party
        // while Whirling Fangs spiral out. Kill (and kick) the cultists.
        abilities: [],
        intermission: {
          maxMs: { base: 15000, atKey: { 7: 25000 } },
          endsWhen: ['addsDead'],
          addNpcIds: [NPC.sacrificialCultist],
          moveTo: 'arenaCenter',
          leapMs: 1000,
          bossAura: immuneRooted(ENEMY_AURA.bloodOffering, 'Blood Offering'),
          landing: [
            areaHit('physical', 8000, 6),
            { type: 'summon', npcId: NPC.sacrificialCultist, count: 2, radius: 16, anchor: 'point' },
            mech({
              kind: 'hazard',
              count: 2,
              zone: {
                type: 'zone',
                zoneId: 'chieftain.whirling_fangs',
                shape: { type: 'circle', radius: 2 },
                anchor: 'caster',
                durationMs: 25000,
                affects: 'enemies',
                uniquePerCaster: false,
                tick: { intervalMs: 1000, effects: [hit('physical', 9000, { avoidable: false, isAoE: true })] },
              },
              motion: { type: 'spiral', angularSpeed: Math.PI / 2, radialSpeed: 1.2, startRadius: 3 },
            }),
          ],
          perSurvivingAdd: [
            { type: 'heal', to: 'self', amount: { pctTargetMaxHp: 0.05 } },
            { type: 'applyAura', to: 'self', aura: fangEmpowerment },
          ],
        },
      },
      {
        abilities: [ENEMY.ritualOfTheFangP2, ENEMY.boneslicer, ENEMY.bloodroar, ENEMY.chopDown, ENEMY.axegrinder, ENEMY.bloodrage],
        // Design §4.3 phase 2 openers.
        firstMs: { [ENEMY.boneslicer]: 6000, [ENEMY.chopDown]: 20000, [ENEMY.axegrinder]: 30000 },
      },
    ],
    enrageMs: ENRAGE_MS,
    arena: ARENA.chieftain,
    markers: { [BRAZIER]: inset(ARENA.chieftain, 5, 5) },
  },
];
